import { useEffect, useMemo, useRef, useState } from "react";
import { apiGet } from "@/shared/api";

interface SyncLastRun {
  module: string;
  interval_seconds: number;
  enabled: boolean;
  last_started_at: string | null;
  last_finished_at: string | null;
  last_status: string | null;
  last_error: string | null;
  running: boolean;
  running_started_at: string | null;
  next_due_at: string | null;
}

interface SyncDetailPanelProps {
  /** Quando ``true`` il pannello e' visibile e fa polling ogni 5s. */
  open: boolean;
}

// Audit 2026-05-28: label rivisitate per riflettere il vero contenuto
// del job. "Messaggi marketplace" stava in basso vicino a "Email
// fornitori": Davide confondeva i due (entrambi sono "mail"). Adesso
// etichetta esplicita + tooltip che riepiloga la sorgente.
const MODULE_LABEL: Record<string, { label: string; hint: string }> = {
  orders: {
    label: "Ordini Poleepo",
    hint: "Pull ordini dal portale Poleepo (REST API)",
  },
  mail: {
    label: "Messaggi marketplace",
    hint: "Thread messaggi Mirakl / Amazon / Metro / ManoMano (no mail fornitori)",
  },
  catalog: {
    label: "Catalogo",
    hint: "Pull catalogo prodotti Poleepo",
  },
  suppliers: {
    label: "Tracking WP Itamoby/Ityhome",
    hint: "Scraper WooCommerce per tracking ordini fornitori (off di default)",
  },
  supplier_mail: {
    label: "Mail fornitori (conferme spedizione)",
    hint: "Scan IMAP Hostinger: conferme ordine, fatture, 'merce pronta al ritiro'",
  },
  as_tracking: {
    label: "Tracking AllService (zip portale)",
    hint: "Download xlsx tracking dall'area riservata AllService",
  },
};

const HIDDEN_WHEN_DISABLED = new Set(["catalog", "suppliers"]);

/**
 * Dettaglio sync per modulo. Risponde alla richiesta di Davide
 * "voglio capire se la sync sta andando davvero": mostra per ogni
 * modulo schedulato quando e' partito l'ultimo run, l'esito, il
 * countdown al prossimo.
 *
 * Restyle 2026-05-28: header con summary aggregato (totale, running,
 * errori) + badge stato leggibili ("OK"/"In corso"/"Errore") al posto
 * dei dot + 2-char "ok/fail". Tipografia coerente, riga groupata per
 * stato (running in cima). Polling 5s quando aperto, sospensione
 * completa quando chiuso.
 */
export function SyncDetailPanel({ open }: SyncDetailPanelProps) {
  const [rows, setRows] = useState<SyncLastRun[]>([]);
  const [tickNow, setTickNow] = useState(() => Date.now());
  const cancelRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    cancelRef.current = false;
    let timerId: number | null = null;

    const tick = async () => {
      try {
        const data = await apiGet<SyncLastRun[]>("/api/sync/last-runs");
        if (cancelRef.current) return;
        setRows(data);
      } catch {
        // Errore silenzioso: il pannello non e' critico, il pill
        // aggregato gia' segnala "Backend irraggiungibile".
      }
      // Audit 2026-05-04 #11: schedula il prossimo tick SOLO se il
      // pannello non e' stato chiuso fra la fetch e qui (race con
      // cleanup). Senza questo guard, anche dopo un errore HTTP il
      // timer veniva programmato post-unmount producendo orphan.
      if (cancelRef.current) return;
      timerId = window.setTimeout(() => void tick(), 5000);
    };
    void tick();

    // Tick separato a 1s per aggiornare i countdown "fra X min" senza
    // rifare la chiamata HTTP (i dati cambiano lentamente, i timestamp
    // relativi devono rinfrescarsi spesso).
    const clockTimer = window.setInterval(() => {
      if (!cancelRef.current) setTickNow(Date.now());
    }, 1000);

    return () => {
      cancelRef.current = true;
      if (timerId !== null) window.clearTimeout(timerId);
      window.clearInterval(clockTimer);
    };
  }, [open]);

  // Filtra le righe nascoste (catalog/suppliers disabilitati) prima di
  // ordinarle: il sort mantiene running > errored > ok > disabled, in
  // modo che cio' che richiede attenzione finisca in cima al pannello.
  const visibleRows = useMemo(() => {
    return rows
      .filter((row) => !(HIDDEN_WHEN_DISABLED.has(row.module) && !row.enabled))
      .slice()
      .sort((a, b) => rowPriority(a) - rowPriority(b));
  }, [rows]);

  // Counter di stato per l'header summary (running / errori). Sono
  // calcolati sulle righe VISIBILI per evitare di mostrare errori di
  // moduli nascosti pre-disabilitazione.
  const summary = useMemo(() => {
    let running = 0;
    let errors = 0;
    let ok = 0;
    for (const row of visibleRows) {
      if (!row.enabled) continue;
      if (row.running) running += 1;
      else if (row.last_status === "failed" || row.last_status === "dead_letter") errors += 1;
      else if (row.last_status === "done") ok += 1;
    }
    return { running, errors, ok, total: visibleRows.length };
  }, [visibleRows]);

  if (!open) return null;

  return (
    <div className="mt-2 rounded-md border border-line bg-card overflow-hidden text-[11.5px] shadow-[0_1px_0_rgba(0,0,0,0.02)]">
      <header className="flex items-center justify-between gap-3 px-3 py-2 border-b border-line bg-paper-2">
        <div className="text-[10px] uppercase tracking-[0.1em] text-ink-3 font-semibold">
          Dettaglio sincronizzazioni
        </div>
        <div className="flex items-center gap-2 text-[10.5px] text-ink-3 font-mono tabular-nums">
          <span>{summary.total} moduli</span>
          {summary.running > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet/10 px-1.5 py-0.5 text-violet font-medium">
              <span className="relative inline-flex w-1.5 h-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-violet opacity-60 animate-ping" />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-violet" />
              </span>
              {summary.running} in corso
            </span>
          )}
          {summary.errors > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-soft px-1.5 py-0.5 text-ink font-medium">
              {summary.errors} in errore
            </span>
          )}
          {summary.errors === 0 && summary.running === 0 && summary.ok > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-soft px-1.5 py-0.5 text-ink font-medium">
              tutti OK
            </span>
          )}
        </div>
      </header>

      <div className="grid grid-cols-[1fr_120px_120px_140px] gap-3 px-3 py-1.5 bg-paper border-b border-line text-ink-3 uppercase tracking-wider text-[9.5px] font-medium">
        <div>Modulo</div>
        <div className="text-right">Ultimo run</div>
        <div className="text-right">Stato</div>
        <div className="text-right">Prossimo</div>
      </div>
      {visibleRows.length === 0 && (
        <div className="px-3 py-3 text-center text-ink-3 italic">Caricamento…</div>
      )}
      {visibleRows.map((row) => (
        <SyncRow key={row.module} row={row} now={tickNow} />
      ))}
    </div>
  );
}

/** Priorita' di ordinamento per sort: piu' basso = piu' in alto. */
function rowPriority(row: SyncLastRun): number {
  if (!row.enabled) return 100;
  if (row.running) return 1;
  if (row.last_status === "failed" || row.last_status === "dead_letter") return 2;
  if (row.last_status === "done") return 10;
  return 20;
}

interface SyncRowProps {
  row: SyncLastRun;
  now: number;
}

function SyncRow({ row, now }: SyncRowProps) {
  const meta = MODULE_LABEL[row.module];
  const label = meta?.label ?? row.module;
  const hint = meta?.hint;
  const lastStartedAt = row.running_started_at || row.last_finished_at || row.last_started_at;
  // Modulo disabilitato: niente "Ultimo run" misleading (era lo storico
  // pre-disabilitazione) né "Prossimo" (non e' schedulato). Lo storico
  // resta accessibile via tooltip sul badge stato.
  const showRunInfo = row.enabled;
  return (
    <div className="grid grid-cols-[1fr_120px_120px_140px] gap-3 px-3 py-2 border-b border-line/40 last:border-0 items-center">
      <div className="flex items-center gap-2 min-w-0" title={hint}>
        <StatusDot row={row} />
        <span className="truncate text-ink">{label}</span>
        {!row.enabled && <span className="text-ink-3 text-[10px]">(disabilitato)</span>}
      </div>
      <div className="text-right text-ink-2 font-mono tabular-nums">
        {showRunInfo ? formatRelative(lastStartedAt, now) || "—" : "—"}
      </div>
      <div className="text-right">
        <StatusBadge row={row} />
      </div>
      <div className="text-right text-ink-2 font-mono tabular-nums">
        {!showRunInfo
          ? "—"
          : row.running
            ? "in corso…"
            : formatRelative(row.next_due_at, now, { future: true }) || "—"}
      </div>
    </div>
  );
}

function StatusDot({ row }: { row: SyncLastRun }) {
  if (!row.enabled) {
    return <span className="w-2 h-2 rounded-full bg-ink-3/40 shrink-0" />;
  }
  if (row.running) {
    return (
      <span className="relative flex w-2 h-2 shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-violet opacity-60 animate-ping" />
        <span className="relative inline-flex w-2 h-2 rounded-full bg-violet" />
      </span>
    );
  }
  if (row.last_status === "done") {
    return <span className="w-2 h-2 rounded-full bg-green shrink-0" />;
  }
  if (row.last_status === "failed" || row.last_status === "dead_letter") {
    return <span className="w-2 h-2 rounded-full bg-red shrink-0" />;
  }
  return <span className="w-2 h-2 rounded-full bg-ink-3/40 shrink-0" />;
}

/** Badge stato leggibile: prima erano 2-char ("ok"/"fail") in colore.
 *  Adesso pillole esplicite con tinta brand-soft per leggibilita' a
 *  colpo d'occhio. Tooltip espone l'errore originale quando rilevante. */
function StatusBadge({ row }: { row: SyncLastRun }) {
  if (!row.enabled) {
    return (
      <span
        className="inline-flex items-center rounded-full bg-paper-2 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-ink-3"
        title={
          row.last_status
            ? `Ultimo esito storico: ${row.last_status}${row.last_error ? ` (${row.last_error})` : ""}`
            : "Modulo disabilitato"
        }
      >
        Off
      </span>
    );
  }
  if (row.running) {
    // Bordo violet/40 per garantire contrasto AA percepito anche su utenti
    // che hanno theme con grigi caldi (paper-2 leggermente shifted). Senza
    // bordo, ``text-violet`` su ``bg-violet/10`` su ``bg-card`` puo'
    // restare percepito come "tenue" → l'occhio scappa.
    return (
      <span className="inline-flex items-center rounded-full bg-violet/10 border border-violet/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-violet">
        In corso
      </span>
    );
  }
  if (row.last_status === "done") {
    return (
      <span className="inline-flex items-center rounded-full bg-green-soft px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-ink">
        OK
      </span>
    );
  }
  if (row.last_status === "failed" || row.last_status === "dead_letter") {
    return (
      <span
        className="inline-flex items-center rounded-full bg-red-soft px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-ink"
        title={row.last_error || "Errore senza messaggio"}
      >
        Errore
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-paper-2 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-ink-3">
      {row.last_status ?? "—"}
    </span>
  );
}

/**
 * Formatta un timestamp ISO come "5 min fa" o "fra 3 min".
 *
 * - Se il timestamp e' nullo o non parsabile, ritorna "".
 * - Sotto il minuto: "ora" / "fra <1m".
 * - Tra 1 min e 60 min: "X min fa" / "fra X min".
 * - Oltre 60 min: "Xh fa" / "fra Xh".
 */
function formatRelative(
  iso: string | null,
  now: number,
  { future = false }: { future?: boolean } = {},
): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  const diffSec = future ? Math.round((ts - now) / 1000) : Math.round((now - ts) / 1000);
  if (diffSec < 0 && future) return "ora"; // gia' scaduto, prossimo run imminente
  if (diffSec < 0) return "ora"; // futuro non atteso → trattalo come "ora"
  if (diffSec < 60) return future ? "<1 min" : "ora";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return future ? `fra ${min} min` : `${min} min fa`;
  const hours = Math.floor(min / 60);
  return future ? `fra ${hours}h` : `${hours}h fa`;
}
